/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { Button } from 'appkit-react';
import { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';

import {
    showReportMenuVar, leftNavigationItemsVar, moduleIdVar, currentLeftMenuVar, tenantIdVar,
} from 'Apollo/config';
import {
    GET_AUDIT_LOGS_FOR_FFIEC009, GET_FAILED_TEST_RESULTS_FOR_FFIEC009, GET_FAILED_TEST_DATA_FOR_FFIEC009,
} from 'Apollo/Queries/Rules/FFIEC009Results';
import crossReportImg from 'assets/images/cross-report-black.svg';
import { PageSizeOptionEnum, ModuleEnum } from 'constants';
import FFIEC009Results from '../components/FFIEC009Results';

const {
    FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE,
} = PageSizeOptionEnum;

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem', color: '#464646' }}>Data Reconciliation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-ticket-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem' }}>Rule Engine</span>,
    ],
    [
        <img
            key={1}
            src={crossReportImg}
            alt="Cross Report"
            style={{
                width: '30.84px', height: '25px', top: '100px',
            }}
        />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Cross Report Validation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-grow-revenue-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Trend Analytics</span>,
    ],
];

const FFIEC009ResultsContainer = () => {
    const ffiec009PageSizeOptions = [FIVE, TEN, FIFTEEN, TWENTY, TWENTYFIVE];
    const location = useLocation();
    const history = useHistory();
    const [ffiec009BatchId, setFfiec009BatchId] = useState(location.state.batchId);
    const [ffiec009RuleId, setFfiec009RuleId] = useState(location.state.ruleId);
    const [ffiec009Data, setFfiec009Data] = useState([]);
    const [ffiec009Pages, setFfiec009Pages] = useState(null);
    const [ffiec009PageSize, setFfiec009PageSize] = useState(TEN);
    const [ffiec009PageNumber, setFfiec009PageNumber] = useState(1);
    const [showResultReportModal, setShowResultReportModal] = useState(false);
    const [rowId, setRowId] = useState('');
    const [rowData, setRowData] = useState([]);
    const [isAggregate, setisAggregate] = useState(false);
    const tenantId = useReactiveVar(tenantIdVar);

    /* Apollo query to fetch failed test data */
    const [getFailedRuleData, { data: failedRowData, loading: failedRowDataLoading }] = useLazyQuery(GET_FAILED_TEST_DATA_FOR_FFIEC009, {
        variables: {
            input: {
                batchId: Number(ffiec009BatchId),
                ruleId: Number(ffiec009RuleId),
                rowId: parseInt(rowId, 10),
                tenantId: parseInt(tenantId, 10),
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Apollo query to fetch audit logs */
    const auditLog = useQuery(GET_AUDIT_LOGS_FOR_FFIEC009, {
        variables: {
            input: {
                batchId: Number(ffiec009BatchId),
                ruleId: Number(ffiec009RuleId),
                tenantId: parseInt(tenantId, 10),
            },
        },
    });

    /* Apollo lazy query to fetch failed test data */
    const [getFailedTestsData, { data: { getFailedTestResultsForFFIEC009 = {} } = {} }] = useLazyQuery(GET_FAILED_TEST_RESULTS_FOR_FFIEC009, {
        variables: {
            input: {
                batchId: Number(ffiec009BatchId),
                ruleId: Number(ffiec009RuleId),
                tenantId: parseInt(tenantId, 10),
                pageSize: ffiec009PageSize,
                pageNumber: ffiec009PageNumber,
                isAggregate,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /** Calls getFailedTestsData() */
    const fetchData = useCallback(
        () => {
            getFailedTestsData();
        },
    );

    /** Calls getFailedRuleData() */
    const openResultReportModal = () => {
        getFailedRuleData();
    };

    /* Column configuration */
    const aggregatedRuleColumns = [
        {
            Header: 'Aggregate MDRM',
            accessor: 'aggregateMdrm',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Aggregate Schedule',
            accessor: 'scheduleType',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Geographical Area',
            accessor: 'geographicalArea',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Error Message',
            accessor: 'aggregateErrorMessage',
            minWidth: 170,
            resizable: true,
            sortable: true,
            style: { whiteSpace: 'unset' },
        },
        {
            Header: 'Aggregated Value',
            accessor: 'aggregation',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Total Value',
            accessor: 'total',
            minWidth: 50,
            resizable: true,
            sortable: true,
        },
    ];

    /* Column configuration */
    const unAggregatedRuleColumns = [
        {
            Header: 'Country Code',
            accessor: 'countryCode',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Country',
            accessor: 'countryName',
            minWidth: 80,
            resizable: true,
            sortable: true,
        }, {
            Header: 'Report',
            accessor: '',
            Cell: row => {
                const { original: { rowId: reportRowId } } = row;
                const onButtonClick = () => {
                    setRowId(reportRowId);
                    openResultReportModal();
                };
                return (
                    <Button size="sm" kind="secondary" onClick={onButtonClick}>Report</Button>
                );
            },
            minWidth: 50,
            resizable: true,
        },
    ];

    const [resultColumns, setResultColumns] = useState(unAggregatedRuleColumns);

    /* Method for sidebar navigation */
    const onNavigate = () => {
        // sidebar navigation
    };

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={() => { history.goBack(); }} role="presentation">FFIEC009 Diagnostic Results</span>,
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">{`Rule #${(ffiec009RuleId) || ''} Results`}</span>,
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        currentLeftMenuVar(ModuleEnum.RULEENGINE);
    }, []);

    /* Side effect when location state updates */
    useEffect(() => {
        setFfiec009BatchId(location.state.batchId);
        setFfiec009RuleId(location.state.ruleId);
        showReportMenuVar(true);
    }, [location]);

    /* Side effect when getFailedTestResultsForFFIEC009 changes */
    useEffect(() => {
        if (getFailedTestResultsForFFIEC009 && getFailedTestResultsForFFIEC009.pagination !== null && Object.keys(getFailedTestResultsForFFIEC009).length > 0) {
            const totalPages = Math.ceil(getFailedTestResultsForFFIEC009.pagination.totalCount / ffiec009PageSize);
            setFfiec009Data(getFailedTestResultsForFFIEC009.testResults);
            setFfiec009Pages(totalPages);
            if (getFailedTestResultsForFFIEC009.criteria.aggregateRule) {
                setResultColumns(aggregatedRuleColumns);
                setisAggregate(true);
            } else {
                setResultColumns(unAggregatedRuleColumns);
                setisAggregate(false);
            }
        }
    }, [getFailedTestResultsForFFIEC009]);

    /* Side effect when failedRowData changes */
    useEffect(() => {
        if (failedRowData && Object.keys(failedRowData.getFailedRuleResultDetailsForFFIEC009).length > 0) {
            setShowResultReportModal(true);
            setRowData(failedRowData.getFailedRuleResultDetailsForFFIEC009);
        }
    }, [failedRowData]);

    return (
        <FFIEC009Results
            breadcrumbItems={breadcrumbItems}
            onNavigate={onNavigate}
            criteria={getFailedTestResultsForFFIEC009 && getFailedTestResultsForFFIEC009.criteria}
            columns={resultColumns}
            fetchData={fetchData}
            data={ffiec009Data}
            pages={ffiec009Pages}
            setPageNumber={setFfiec009PageNumber}
            pagesize={ffiec009PageSize}
            setPageSize={setFfiec009PageSize}
            refetch={getFailedTestsData}
            auditLog={auditLog}
            showResultReportModal={showResultReportModal}
            setShowResultReportModal={setShowResultReportModal}
            rowData={rowData}
            failedRowDataLoading={failedRowDataLoading}
            pageSizeOptions={ffiec009PageSizeOptions}
        />
    );
};

export default FFIEC009ResultsContainer;
